import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TenantThemeConfig } from '../types/theme'
import { getThemeById } from '../config/themes'
import HuayLobbyPromoArt from '../components/HuayLobbyPromoArt'
import './ModernLobbyCasinoPage.css'
import {
  IconAll,
  IconBall,
  IconCasino,
  IconFishing,
  IconHuay,
  IconPoker,
  IconPopular,
  IconSlot,
} from './LobbyCategoryIcons'
import type { LobbyCategoryId } from './LobbyCategoryIcons'
import * as casinoService from '../services/casinoService'
import type { PopularGame } from '../services/casinoService'
import type { Provider, Game } from '../services/casinoService'

interface AssetConfig {
  CDN_BASE_URL: string
  CSS_PATH: string
  IMG_PATH: string
  FONTS_PATH: string
}

interface ModernLobbyCasinoPageProps {
  themeConfig: TenantThemeConfig
  assetConfig: AssetConfig
}

const IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'

// ─── Category config ─────────────────────────────────────────────────────────

interface CategoryDef {
  id: LobbyCategoryId
  label: string
  Icon: React.FC
  /** AMB category key — undefined = show all */
  ambCategory?: string
}

const CATEGORIES: CategoryDef[] = [
  { id: 'all',     label: 'ทั้งหมด',  Icon: IconAll,     ambCategory: undefined },
  { id: 'casino',  label: 'คาสิโน',   Icon: IconCasino,  ambCategory: 'LIVECASINO' },
  { id: 'slot',    label: 'สล็อต',    Icon: IconSlot,    ambCategory: 'EGAMES' },
  { id: 'poker',   label: 'ป๊อกเด้ง', Icon: IconPoker,   ambCategory: 'POKER' },
  { id: 'fishing', label: 'ยิงปลา',   Icon: IconFishing, ambCategory: 'KENO' },
  { id: 'huay',    label: 'หวย',      Icon: IconHuay,    ambCategory: 'HUAY' },
  { id: 'ball',    label: 'บอล',      Icon: IconBall,    ambCategory: 'SPORT' },
  { id: 'popular', label: 'กำลังนิยม',Icon: IconPopular, ambCategory: undefined },
]

const ALL_PROVIDERS_ID = '__all__'

// ─── Game session state ──────────────────────────────────────────────────────

interface ActiveGame {
  url: string
  gameName: string
  providerName: string
  gameCode: string
  providerId: string
  /** true = game was opened in a new tab (HTTP game on HTTPS page) */
  openedInNewTab?: boolean
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Spinner({ size = 24 }: { size?: number }) {
  return (
    <svg
      style={{ width: size, height: size, animation: 'spin 1s linear infinite' }}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  )
}

/**
 * GamePlayerOverlay
 * Full-screen iframe overlay — shows while player is inside a game.
 *
 * When a game provider (e.g. SpinGo) navigates the iframe to callbackUrl after each spin,
 * we do NOT close the overlay. Instead we automatically call onRelaunch() to get a fresh
 * session URL and reload the game in-place — eliminating the 15-second re-entry delay.
 *
 * The overlay only closes when the user explicitly presses "← ออกจากเกม" button.
 */
function GamePlayerOverlay({
  game,
  onExit,
  onRelaunch,
}: {
  game: ActiveGame
  onExit: () => void
  onRelaunch: () => Promise<void>
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [relaunching, setRelaunching] = useState(false)
  const relaunchingRef = useRef(false)

  const handleIframeLoad = useCallback(() => {
    try {
      // Accessing contentWindow.location throws SecurityError for cross-origin (game server).
      // If it succeeds → same-origin → game navigated to callbackUrl (our domain).
      const href = iframeRef.current?.contentWindow?.location?.href ?? ''

      // Ignore about:blank (transient empty page during internal game transitions).
      if (!href || href === 'about:blank') return

      if (href.startsWith(window.location.origin)) {
        // Game navigated to our callbackUrl. Some providers (e.g. SpinGo) do this
        // automatically after every spin — NOT only when the player presses "Exit".
        // Auto-reload the game session so the player can spin again immediately
        // without having to go back to the lobby and re-enter (saves ~15 seconds).
        if (relaunchingRef.current) return
        relaunchingRef.current = true
        setRelaunching(true)
        onRelaunch().finally(() => {
          relaunchingRef.current = false
          setRelaunching(false)
        })
      }
    } catch {
      // Cross-origin during gameplay — ignore silently
    }
  }, [onRelaunch])

  // Lock body scroll while game is open
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Top bar ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.5rem 1rem',
          background: 'rgba(10,10,20,0.97)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
          minHeight: 48,
        }}
      >
        <button
          onClick={onExit}
          style={{
            background: 'rgba(239,68,68,0.15)',
            border: '1px solid rgba(239,68,68,0.5)',
            color: '#fca5a5',
            padding: '0.35rem 0.9rem',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            fontWeight: 700,
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
          }}
        >
          ← ออกจากเกม
        </button>

        <div
          style={{
            flex: 1,
            textAlign: 'center',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.95rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {game.providerName}
          {game.gameName && game.gameName !== game.providerName && (
            <span style={{ opacity: 0.6, fontWeight: 400 }}> — {game.gameName}</span>
          )}
        </div>

        {/* Spacer to balance the exit button */}
        <div style={{ width: 100 }} />
      </div>

      {/* ── Game content ── */}
      <div style={{ flex: 1, position: 'relative' }}>
        {game.openedInNewTab ? (
          /* ── New-tab game notice (HTTP game on HTTPS page) ── */
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 50%, #0f0f1a 100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1.5rem',
              padding: '2rem',
              textAlign: 'center',
            }}
          >
            {/* Game icon */}
            <div style={{
              width: 80, height: 80, borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 36, boxShadow: '0 0 40px rgba(99,102,241,0.4)',
            }}>
              🎮
            </div>

            <div>
              <div style={{ color: '#fff', fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>
                {game.gameName || game.providerName}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                {game.providerName}
              </div>
            </div>

            <div style={{
              background: 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(99,102,241,0.4)',
              borderRadius: 12,
              padding: '1rem 1.5rem',
              maxWidth: 360,
            }}>
              <div style={{ color: '#a5b4fc', fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                🎯 เกมส์กำลังเล่นอยู่ใน Tab อื่น
              </div>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', lineHeight: 1.6 }}>
                กลับไปที่ Tab เกมส์ที่เปิดไว้<br />
                กดปุ่ม "กลับไปเกมส์" เพื่อเปิดอีกครั้ง<br />
                หรือกด "ออกจากเกม" เมื่อเล่นเสร็จแล้ว
              </div>
            </div>

            <div style={{ color: 'rgba(239,68,68,0.8)', fontSize: '0.8rem', fontWeight: 600 }}>
              ⚠️ ไม่สามารถถอนเงินได้ขณะที่เล่นเกมส์อยู่
            </div>

            {/* Return to game button */}
            <button
              onClick={() => window.open(game.url, '_blank', 'noopener,noreferrer')}
              style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                border: 'none',
                color: '#fff',
                padding: '0.75rem 2rem',
                borderRadius: 10,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: '0.95rem',
                boxShadow: '0 4px 20px rgba(99,102,241,0.4)',
              }}
            >
              🎮 กลับไปเกมส์
            </button>
          </div>
        ) : (
          /* ── Normal iframe game ── */
          <>
            <iframe
              ref={iframeRef}
              src={game.url}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', background: '#111' }}
              allow="fullscreen; autoplay; screen-wake-lock; clipboard-write; payment; gyroscope; accelerometer"
              allowFullScreen
              onLoad={handleIframeLoad}
              title={game.gameName || 'Game'}
            />

            {/* Relaunch loading overlay */}
            {relaunching && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0,0,0,0.85)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem',
                  color: '#fff',
                  zIndex: 1,
                }}
              >
                <Spinner size={40} />
                <div style={{ fontSize: '1rem', fontWeight: 600 }}>กำลังโหลดเกมใหม่...</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function GameCard({
  game,
  onPlay,
  launching,
}: {
  game: Game
  onPlay: () => void
  launching: boolean
}) {
  const [imgError, setImgError] = useState(false)

  return (
    <div className="col-6 col-md-3 col-xl-2">
      <div
        className="d-grid lobby_link gap-2"
        style={{ cursor: launching ? 'wait' : 'pointer', position: 'relative' }}
        onClick={onPlay}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onPlay()}
      >
        <div className="img_" style={{ position: 'relative', overflow: 'hidden' }}>
          <img
            src={!imgError && game.imageUrl ? game.imageUrl : IMG_PLACEHOLDER}
            alt={game.name}
            onError={() => setImgError(true)}
            style={{ width: '100%', display: 'block' }}
            loading="lazy"
          />
          {typeof game.rank === 'number' && game.rank > 0 && game.rank <= 10 && (
            <div
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                background: '#facc15',
                color: '#713f12',
                fontSize: '0.65rem',
                fontWeight: 800,
                padding: '0.18rem 0.45rem',
                borderRadius: 999,
                boxShadow: '0 4px 10px rgba(0,0,0,0.18)',
                zIndex: 1,
              }}
            >
              TOP {game.rank}
            </div>
          )}
          {launching && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <Spinner size={28} />
            </div>
          )}
          <div
            className="game-play-overlay"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0,
              transition: 'opacity 0.2s',
              color: '#fff',
              fontSize: 32,
            }}
          >
            ▶
          </div>
        </div>
        <h5 className="text-center txt_ c_tertiary" style={{ fontSize: '0.75rem', margin: 0 }}>
          {game.name}
        </h5>
      </div>
    </div>
  )
}

function LoginPrompt({ onLogin }: { onLogin: () => void }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '3rem 1rem',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
      }}
    >
      <div style={{ fontSize: 48 }}>🎰</div>
      <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>กรุณาเข้าสู่ระบบเพื่อเล่นเกม</div>
      <div style={{ opacity: 0.7, fontSize: '0.9rem' }}>เข้าสู่ระบบเพื่อดูเกมและเริ่มเล่นได้เลย</div>
      <button
        className="btn"
        style={{
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          color: '#fff',
          fontWeight: 700,
          padding: '0.6rem 2rem',
          borderRadius: 8,
          border: 'none',
        }}
        onClick={onLogin}
      >
        เข้าสู่ระบบ
      </button>
    </div>
  )
}

function HuayLobbyPrompt({ siteName }: { siteName: string }) {
  const navigate = useNavigate()

  return (
    <div className="lobby-casino-huay-prompt">
      <div className="lobby-casino-huay-prompt__art" aria-hidden="true">
        <HuayLobbyPromoArt brandLabel={siteName} />
      </div>
      <h5 className="lobby-casino-huay-prompt__title c_tertiary font_md mb-0">แทงหวยออนไลน์</h5>
      <p className="lobby-casino-huay-prompt__desc c_tertiary mb-0">
        เลือกรอบหวย แทงได้ทุกประเภท รัฐบาล ยี่กี หวยต่างประเทศ และอื่นๆ
      </p>
      <button
        type="button"
        className="lobby-casino-huay-prompt__btn"
        onClick={() => navigate('/lobby-huay/listall')}
      >
        แทงหวย
      </button>
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '3rem 1rem',
        color: 'rgba(255,255,255,0.6)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '0.75rem',
      }}
    >
      <div style={{ fontSize: 40 }}>🎮</div>
      <div>{message}</div>
    </div>
  )
}

function ProviderLogo({
  provider,
  size = 28,
  variant = 'default',
}: {
  provider: Provider
  size?: number
  variant?: 'default' | 'tab' | 'card'
}) {
  const [imgError, setImgError] = useState(false)
  const accentPalette = ['#3b82f6', '#8b5cf6', '#06b6d4', '#22c55e', '#f59e0b', '#ef4444']
  const accent = accentPalette[
    Array.from(provider.providerId || provider.name || 'X').reduce((sum, char) => sum + char.charCodeAt(0), 0) % accentPalette.length
  ]
  const containerRadius = variant === 'card' ? 20 : 12
  const containerPadding = variant === 'card' ? Math.max(10, Math.round(size * 0.16)) : Math.max(4, Math.round(size * 0.1))
  const fallbackFontSize = variant === 'card' ? Math.max(18, size * 0.24) : Math.max(11, size * 0.34)

  const frameStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: containerRadius,
    background:
      variant === 'card'
        ? `radial-gradient(circle at top, ${accent}33 0%, rgba(255,255,255,0.08) 42%, rgba(255,255,255,0.02) 100%)`
        : `linear-gradient(145deg, rgba(255,255,255,0.14), rgba(255,255,255,0.04))`,
    border: `1px solid ${variant === 'card' ? `${accent}55` : 'rgba(255,255,255,0.12)'}`,
    boxShadow:
      variant === 'card'
        ? `0 16px 34px rgba(0,0,0,0.28), 0 0 30px ${accent}22`
        : `0 8px 18px rgba(0,0,0,0.22)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
    backdropFilter: 'blur(10px)',
  }

  if (provider.logoUrl && !imgError) {
    return (
      <div style={frameStyle}>
        <img
          src={provider.logoUrl}
          alt={provider.name}
          onError={() => setImgError(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            padding: containerPadding,
            filter: variant === 'card' ? 'drop-shadow(0 10px 16px rgba(0,0,0,0.28))' : 'none',
          }}
        />
      </div>
    )
  }

  return (
    <div
      style={{
        ...frameStyle,
        background: `linear-gradient(135deg, ${accent}, #0f172a)`,
        color: '#fff',
        fontSize: fallbackFontSize,
        fontWeight: 800,
        letterSpacing: '0.06em',
        textShadow: '0 2px 8px rgba(0,0,0,0.24)',
      }}
    >
      {provider.name.slice(0, 2).toUpperCase()}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const ModernLobbyCasinoPage: React.FC<ModernLobbyCasinoPageProps> = ({ themeConfig, assetConfig }) => {
  const tenantId = themeConfig.tenantId
  const themeFolder = useMemo(
    () => getThemeById(themeConfig.themeId)?.folder ?? 'HuayToday-Blue',
    [themeConfig.themeId],
  )

  const getAssetUrl = useCallback(
    (path: string) => {
      const base = assetConfig.CDN_BASE_URL
        ? `${assetConfig.CDN_BASE_URL}/${themeFolder}`
        : `/${themeFolder}`
      return base + (path.startsWith('/') ? path : `/${path}`)
    },
    [assetConfig.CDN_BASE_URL, themeFolder],
  )

  // suppress unused warning
  void getAssetUrl

  // ─── Auth state ────────────────────────────────────────────────────────────
  const [loggedIn, setLoggedIn] = useState(() => casinoService.isLoggedIn())

  // ─── Data state ───────────────────────────────────────────────────────────
  const [providers, setProviders] = useState<Provider[]>([])
  const [gamesCache, setGamesCache] = useState<Record<string, Game[]>>({})
  const [loadingProviders, setLoadingProviders] = useState(false)
  const [loadingGames, setLoadingGames] = useState(false)
  const [providersError, setProvidersError] = useState<string | null>(null)
  const [gamesError, setGamesError] = useState<string | null>(null)
  const [launchingGame, setLaunchingGame] = useState<string | null>(null)
  const [launchError, setLaunchError] = useState<string | null>(null)

  // ─── UI state ─────────────────────────────────────────────────────────────
  const [activeCategory, setActiveCategory] = useState<LobbyCategoryId>('all')
  const [activeProviderId, setActiveProviderId] = useState<string>(ALL_PROVIDERS_ID)
  const [gameSearch, setGameSearch] = useState('')

  // ─── Game overlay state ───────────────────────────────────────────────────
  // null = lobby shown; object = game iframe shown
  const [activeGame, setActiveGame] = useState<ActiveGame | null>(null)

  // ─── Popular games state ──────────────────────────────────────────────────
  const [popularGames, setPopularGames]       = useState<PopularGame[]>([])
  const [popularLoading, setPopularLoading]   = useState(false)
  const [popularMode, setPopularMode]         = useState<'alltime' | 'weekly'>('alltime')

  // Track in-flight fetches (avoid duplicate)
  const loadingGameRef = useRef<Set<string>>(new Set())
  // Track already-fetched providers so we don't depend on gamesCache in callbacks
  const fetchedProvidersRef = useRef<Set<string>>(new Set())

  // ─── Derived data ─────────────────────────────────────────────────────────
  const activeCategoryDef = CATEGORIES.find((c) => c.id === activeCategory) ?? CATEGORIES[0]

  const filteredProviders = useMemo(() => {
    if (!activeCategoryDef.ambCategory) return providers
    if (activeCategoryDef.id === 'huay') return []
    return providers.filter((p) => p.category?.toUpperCase() === activeCategoryDef.ambCategory)
  }, [providers, activeCategoryDef])

  const activeProvider = useMemo(
    () => providers.find((p) => p.providerId === activeProviderId) ?? null,
    [providers, activeProviderId],
  )

  const displayedGames = useMemo(
    () => (activeProviderId !== ALL_PROVIDERS_ID ? (gamesCache[activeProviderId] ?? []) : []),
    [activeProviderId, gamesCache],
  )

  const searchedGames = useMemo(() => {
    const q = gameSearch.trim().toLowerCase()
    if (!q) return displayedGames
    return displayedGames.filter((g) => g.name.toLowerCase().includes(q))
  }, [displayedGames, gameSearch])

  // ─── Load providers ───────────────────────────────────────────────────────
  const loadProviders = useCallback(async () => {
    if (!loggedIn) return
    setLoadingProviders(true)
    setProvidersError(null)
    try {
      const data = await casinoService.getProviders(tenantId)
      setProviders(data)
    } catch (err) {
      setProvidersError('ไม่สามารถโหลดข้อมูลผู้ให้บริการได้')
      console.error('Casino getProviders error:', err)
    } finally {
      setLoadingProviders(false)
    }
  }, [tenantId, loggedIn])

  useEffect(() => {
    loadProviders()
  }, [loadProviders])

  // ─── Load popular games ───────────────────────────────────────────────────
  const loadPopularGames = useCallback(async (mode: 'alltime' | 'weekly' = popularMode) => {
    if (!loggedIn) return
    setPopularLoading(true)
    try {
      const res = await casinoService.getPopularGames(tenantId, 30, mode)
      setPopularGames(res.data)
    } catch (err) {
      console.error('Popular games error:', err)
    } finally {
      setPopularLoading(false)
    }
  }, [tenantId, loggedIn, popularMode])

  useEffect(() => {
    if (activeCategory === 'popular') loadPopularGames(popularMode)
  }, [activeCategory, popularMode, loadPopularGames])

  // ─── Load games for a provider ────────────────────────────────────────────
  const loadGames = useCallback(
    async (providerId: string) => {
      if (!loggedIn) return
      if (providerId === ALL_PROVIDERS_ID) return
      if (fetchedProvidersRef.current.has(providerId)) return
      if (loadingGameRef.current.has(providerId)) return
      fetchedProvidersRef.current.add(providerId)
      loadingGameRef.current.add(providerId)
      setLoadingGames(true)
      setGamesError(null)
      try {
        const games = await casinoService.getProviderGames(tenantId, providerId)
        setGamesCache((prev) => ({ ...prev, [providerId]: games }))
      } catch (err) {
        fetchedProvidersRef.current.delete(providerId)
        setGamesError('ไม่สามารถโหลดรายการเกมได้')
        console.error('Casino getProviderGames error:', err)
      } finally {
        loadingGameRef.current.delete(providerId)
        setLoadingGames(false)
      }
    },
    [tenantId, loggedIn],
  )

  const refreshCatalog = useCallback(async () => {
    fetchedProvidersRef.current.clear()
    loadingGameRef.current.clear()
    setGamesCache({})
    await loadProviders()

    if (activeProviderId !== ALL_PROVIDERS_ID) {
      await loadGames(activeProviderId)
    }
  }, [activeProviderId, loadGames, loadProviders])

  useEffect(() => {
    if (activeProviderId !== ALL_PROVIDERS_ID) {
      loadGames(activeProviderId)
    }
  }, [activeProviderId, loadGames])

  useEffect(() => {
    if (activeProviderId !== ALL_PROVIDERS_ID && !providers.some((provider) => provider.providerId === activeProviderId)) {
      setActiveProviderId(ALL_PROVIDERS_ID)
    }
  }, [activeProviderId, providers])

  // ─── Category / provider navigation ───────────────────────────────────────
  const handleCategoryChange = useCallback((catId: LobbyCategoryId) => {
    setActiveCategory(catId)
    setActiveProviderId(ALL_PROVIDERS_ID)
    setGameSearch('')
    setGamesError(null)
    setLaunchError(null)
  }, [])

  const handleProviderChange = useCallback((providerId: string) => {
    setActiveProviderId(providerId)
    setGameSearch('')
    setGamesError(null)
    setLaunchError(null)
  }, [])

  // ─── Launch game (open in-tab iframe) ─────────────────────────────────────
  const handlePlayGame = useCallback(
    async (game: Game, provider: Provider) => {
      if (!loggedIn) return
      const key = `${provider.providerId}:${game.gameCode}`
      if (launchingGame) return
      setLaunchingGame(key)
      setLaunchError(null)
      try {
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

        // callbackUrl = our SPA base URL; the game iframe will navigate here on exit
        // which triggers same-origin detection and auto-closes the overlay
        const callbackUrl = window.location.origin + window.location.pathname

        const result = await casinoService.launchGame(tenantId, provider, game, isMobile, callbackUrl)

        // Track play count (fire-and-forget — never blocks gameplay)
        casinoService.trackGamePlay(tenantId, game, provider)

        // Record session in localStorage — blocks withdrawal while in game
        casinoService.setGameSession({
          active: true,
          gameCode: game.gameCode,
          gameName: game.name,
          providerName: provider.name,
          startTime: Date.now(),
        })

        // Some providers (e.g. ACE333) return HTTP URLs.
        // Embedding HTTP in an HTTPS page causes Mixed Content block.
        // Open in a new tab BUT keep the session active (blocks withdrawal)
        // and show a "playing in another tab" overlay so the user must
        // explicitly press "ออกจากเกม" to end the session.
        const isHttpGame = result.gameUrl.startsWith('http://')
        const isHttpsPage = window.location.protocol === 'https:'

        if (isHttpGame && isHttpsPage) {
          window.open(result.gameUrl, '_blank', 'noopener,noreferrer')
          // Show the overlay in "new tab" mode — session stays blocked
          setActiveGame({
            url: result.gameUrl,
            gameName: game.name,
            providerName: provider.name,
            gameCode: game.gameCode,
            providerId: provider.providerId,
            openedInNewTab: true,
          })
          return
        }

        // Open game inside the same tab via fullscreen iframe overlay
        setActiveGame({
          url: result.gameUrl,
          gameName: game.name,
          providerName: provider.name,
          gameCode: game.gameCode,
          providerId: provider.providerId,
        })
      } catch (err) {
        console.error('Casino launchGame error:', err)
        setLaunchError(err instanceof Error ? err.message : 'ไม่สามารถเปิดเกมได้ กรุณาลองใหม่')
      } finally {
        setLaunchingGame(null)
      }
    },
    [tenantId, loggedIn, launchingGame],
  )

  // ─── Relaunch game (auto-reload after provider navigates to callbackUrl) ─────
  // Some providers (e.g. SpinGo) navigate the iframe to callbackUrl after each spin.
  // Instead of closing the overlay (which forces the user back to lobby for 15s),
  // we silently refresh the seamless session and update the iframe src in-place.
  const handleRelaunchGame = useCallback(async () => {
    if (!activeGame) return
    const provider = providers.find((p) => p.providerId === activeGame.providerId)
    const game = (gamesCache[activeGame.providerId] ?? []).find(
      (g) => g.gameCode === activeGame.gameCode,
    )
    if (!provider || !game) {
      casinoService.clearGameSession()
      setActiveGame(null)
      return
    }
    try {
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
      const callbackUrl = window.location.origin + window.location.pathname
      const result = await casinoService.launchGame(tenantId, provider, game, isMobile, callbackUrl)
      setActiveGame((prev) => (prev ? { ...prev, url: result.gameUrl } : null))
    } catch (err) {
      console.error('Casino relaunchGame error:', err)
      casinoService.clearGameSession()
      setActiveGame(null)
    }
  }, [activeGame, providers, gamesCache, tenantId])

  // ─── Exit game (close iframe, return to lobby) ─────────────────────────────
  const handleExitGame = useCallback(() => {
    casinoService.clearGameSession()
    setActiveGame(null)
  }, [])

  // ─── Navigate to login ─────────────────────────────────────────────────────
  const handleGoLogin = useCallback(() => {
    window.location.hash = '/login'
  }, [])

  // ─── Check auth on storage change (e.g. login in another tab) ─────────────
  useEffect(() => {
    const onStorage = () => setLoggedIn(casinoService.isLoggedIn())
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    let isRefreshing = false

    const handleGameControlUpdated = async (event: Event) => {
      const detail = (event as CustomEvent<{ tenantId?: string }>).detail
      if (detail?.tenantId && detail.tenantId !== tenantId) {
        return
      }
      if (isRefreshing) return

      isRefreshing = true
      try {
        await refreshCatalog()
      } finally {
        isRefreshing = false
      }
    }

    window.addEventListener('game_control_updated', handleGameControlUpdated)
    return () => window.removeEventListener('game_control_updated', handleGameControlUpdated)
  }, [refreshCatalog, tenantId])

  // ─── Rendering helpers ────────────────────────────────────────────────────

  const renderProviderTabs = () => {
    const providerList = filteredProviders.length > 0 ? filteredProviders : providers
    return (
      <div className="nav nav-pills nav_pills_tab_game" id="v-pills-tab-lobby" role="tablist">
        <button
          type="button"
          className={`nav-link ${activeProviderId === ALL_PROVIDERS_ID ? 'active' : ''}`}
          onClick={() => handleProviderChange(ALL_PROVIDERS_ID)}
        >
          ทั้งหมด
        </button>
        {providerList.map((p) => (
          <button
            key={p.providerId}
            type="button"
            className={`nav-link ${activeProviderId === p.providerId ? 'active' : ''}`}
            onClick={() => handleProviderChange(p.providerId)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.55rem',
              paddingRight: '0.9rem',
              minHeight: 44,
              borderRadius: 14,
              border: activeProviderId === p.providerId ? '1px solid rgba(255,255,255,0.14)' : '1px solid transparent',
              background: activeProviderId === p.providerId ? 'rgba(255,255,255,0.08)' : 'transparent',
            }}
          >
            <ProviderLogo provider={p} size={28} variant="tab" />
            <span style={{ fontWeight: 700 }}>{p.name}</span>
          </button>
        ))}
      </div>
    )
  }

  const renderGamesGrid = () => {
    if (!loggedIn) return <LoginPrompt onLogin={handleGoLogin} />

    if (activeCategory === 'huay') {
      return <HuayLobbyPrompt siteName={themeConfig.theme.branding.siteName} />
    }

    if (loadingProviders) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#fff' }}>
          <Spinner size={36} />
          <div style={{ marginTop: 12 }}>กำลังโหลดผู้ให้บริการ...</div>
        </div>
      )
    }

    if (providersError) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f87171' }}>
          <div>{providersError}</div>
          <button
            style={{ marginTop: 12, padding: '0.4rem 1.2rem', borderRadius: 6, border: 'none', cursor: 'pointer' }}
            onClick={loadProviders}
          >
            ลองใหม่
          </button>
        </div>
      )
    }

    if (
      filteredProviders.length === 0 &&
      providers.length > 0 &&
      activeCategory !== 'all' &&
      activeCategory !== 'popular'
    ) {
      return <EmptyState message="ไม่มีเกมในหมวดหมู่นี้" />
    }

    // ── "กำลังนิยม" tab ───────────────────────────────────────────────────
    if (activeCategory === 'popular') {
      return (
        <div className="lobby_heigth" style={{ padding: '0.75rem 0.25rem' }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', paddingLeft: 4 }}>
            {(['alltime', 'weekly'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setPopularMode(m)}
                style={{
                  padding: '0.3rem 1rem',
                  borderRadius: 20,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  background: popularMode === m
                    ? 'linear-gradient(135deg,#f59e0b,#d97706)'
                    : 'rgba(255,255,255,0.1)',
                  color: popularMode === m ? '#fff' : 'rgba(255,255,255,0.6)',
                  transition: 'all 0.2s',
                }}
              >
                {m === 'alltime' ? '🏆 ตลอดกาล' : '🔥 7 วันนี้'}
              </button>
            ))}
            <button
              onClick={() => loadPopularGames(popularMode)}
              disabled={popularLoading}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'rgba(255,255,255,0.6)',
                borderRadius: 20,
                padding: '0.25rem 0.75rem',
                cursor: 'pointer',
                fontSize: '0.75rem',
              }}
            >
              {popularLoading ? '⏳' : '🔄'} รีเฟรช
            </button>
          </div>

          {/* Loading state */}
          {popularLoading && (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.5)' }}>
              <Spinner size={36} />
              <div style={{ marginTop: '0.75rem', fontSize: '0.85rem' }}>กำลังโหลด...</div>
            </div>
          )}

          {/* Empty state */}
          {!popularLoading && popularGames.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: 'rgba(255,255,255,0.5)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>ยังไม่มีข้อมูลการเล่น</div>
              <div style={{ fontSize: '0.8rem' }}>เริ่มเล่นเกมส์เพื่อดูอันดับความนิยม</div>
            </div>
          )}

          {/* Popular game list */}
          {!popularLoading && popularGames.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {popularGames.map((pg, idx) => {
                // bar color by rank
                const barColor = idx === 0 ? '#f59e0b' : idx === 1 ? '#9ca3af' : idx === 2 ? '#b45309' : '#6366f1'
                const rankEmoji = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${pg.rank}`

                // Find matching provider + game for launch
                const provider = providers.find((p) => p.providerId === pg.providerId)
                const games    = gamesCache[pg.providerId] || []
                const game     = games.find((g) => g.gameCode === pg.gameCode)

                const canLaunch = !!provider && !!game && loggedIn

                return (
                  <div
                    key={`${pg.providerId}-${pg.gameCode}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      background: 'rgba(255,255,255,0.05)',
                      borderRadius: 10,
                      padding: '0.6rem 0.75rem',
                      border: idx < 3 ? `1px solid ${barColor}33` : '1px solid transparent',
                    }}
                  >
                    {/* Rank */}
                    <div style={{
                      minWidth: 32,
                      textAlign: 'center',
                      fontSize: idx < 3 ? '1.25rem' : '0.85rem',
                      fontWeight: 700,
                      color: idx < 3 ? barColor : 'rgba(255,255,255,0.4)',
                    }}>
                      {rankEmoji}
                    </div>

                    {/* Game image */}
                    {pg.imageUrl ? (
                      <img
                        src={pg.imageUrl}
                        alt={pg.gameName}
                        style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                    ) : (
                      <div style={{
                        width: 40, height: 40, borderRadius: 6,
                        background: 'rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, flexShrink: 0,
                      }}>🎮</div>
                    )}

                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        color: '#fff',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {pg.gameName || pg.gameCode}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
                        {pg.providerName}
                      </div>
                      {/* Progress bar */}
                      <div style={{
                        marginTop: 4,
                        height: 4,
                        borderRadius: 2,
                        background: 'rgba(255,255,255,0.1)',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${Math.min(100, pg.percentage)}%`,
                          background: barColor,
                          borderRadius: 2,
                          transition: 'width 0.6s ease',
                        }} />
                      </div>
                    </div>

                    {/* Percentage */}
                    <div style={{
                      minWidth: 44,
                      textAlign: 'right',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      color: barColor,
                    }}>
                      {pg.percentage.toFixed(1)}%
                    </div>

                    {/* Play count */}
                    <div style={{
                      minWidth: 36,
                      textAlign: 'right',
                      fontSize: '0.7rem',
                      color: 'rgba(255,255,255,0.35)',
                    }}>
                      {pg.count.toLocaleString()}<br />ครั้ง
                    </div>

                    {/* Launch button */}
                    <button
                      onClick={() => canLaunch && handlePlayGame(game!, provider!)}
                      disabled={!canLaunch || !!launchingGame}
                      style={{
                        flexShrink: 0,
                        background: canLaunch
                          ? 'linear-gradient(135deg,#f59e0b,#d97706)'
                          : 'rgba(255,255,255,0.1)',
                        border: 'none',
                        color: canLaunch ? '#fff' : 'rgba(255,255,255,0.3)',
                        borderRadius: 8,
                        padding: '0.35rem 0.7rem',
                        cursor: canLaunch ? 'pointer' : 'default',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                      }}
                    >
                      {launchingGame === `${provider?.providerId}:${pg.gameCode}` ? '⏳' : '▶ เล่น'}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )
    }

    // ── "ทั้งหมด" tab: show provider cards ────────────────────────────────
    if (activeProviderId === ALL_PROVIDERS_ID) {
      const providersToShow = filteredProviders.length > 0 ? filteredProviders : providers
      if (providersToShow.length === 0) {
        return <EmptyState message="ไม่มีผู้ให้บริการเกมที่เปิดใช้งาน" />
      }
      return (
        <div className="lobby_heigth d-grid gap-3 p-1">
          <div className="row" style={{ rowGap: '1rem', margin: 0 }}>
            {providersToShow.map((p) => (
              <div key={p.providerId} className="col-6 col-md-4 col-xl-3">
                <button
                  type="button"
                  onClick={() => handleProviderChange(p.providerId)}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 20,
                    padding: '1rem',
                    color: '#fff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                    minHeight: 190,
                    boxShadow: '0 18px 35px rgba(0,0,0,0.22)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)'
                    e.currentTarget.style.boxShadow = '0 22px 40px rgba(0,0,0,0.3)'
                    e.currentTarget.style.borderColor = 'rgba(96,165,250,0.42)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.boxShadow = '0 18px 35px rgba(0,0,0,0.22)'
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'radial-gradient(circle at top right, rgba(59,130,246,0.18), transparent 38%)',
                      pointerEvents: 'none',
                    }}
                  />
                  <div
                    style={{
                      marginBottom: 14,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 96,
                      borderRadius: 18,
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <ProviderLogo provider={p} size={88} variant="card" />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
                    <div style={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.25 }}>{p.name}</div>
                    <div
                      style={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        padding: '0.24rem 0.55rem',
                        borderRadius: 999,
                        background: 'rgba(255,255,255,0.08)',
                        color: 'rgba(255,255,255,0.72)',
                        flexShrink: 0,
                      }}
                    >
                      {p.category || 'คาสิโน'}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.72 }}>
                    {p.gameCount > 0 ? `${p.gameCount} เกม` : 'คลิกเพื่อดูเกม'}
                  </div>
                  <div style={{ marginTop: 14, fontSize: '0.72rem', fontWeight: 700, color: 'rgba(147,197,253,0.95)' }}>
                    ดูเกมทั้งหมด →
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      )
    }

    // ── Specific provider: loading / error / game grid ─────────────────────
    if (loadingGames && gamesCache[activeProviderId] === undefined) {
      return (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#fff' }}>
          <Spinner size={36} />
          <div style={{ marginTop: 12 }}>กำลังโหลดเกม...</div>
        </div>
      )
    }

    if (gamesError) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f87171' }}>
          <div>{gamesError}</div>
        </div>
      )
    }

    if (searchedGames.length === 0) {
      return <EmptyState message={gameSearch ? `ไม่พบเกม "${gameSearch}"` : 'ไม่มีเกมที่เปิดใช้งาน'} />
    }

    return (
      <div className="lobby_heigth">
        <div className="row" style={{ rowGap: '1rem', margin: 0 }}>
          {searchedGames.map((game) => (
            <GameCard
              key={game.gameCode}
              game={game}
              launching={launchingGame === `${activeProvider?.providerId}:${game.gameCode}`}
              onPlay={() => activeProvider && handlePlayGame(game, activeProvider)}
            />
          ))}
        </div>
      </div>
    )
  }

  // ─── JSX ──────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Game iframe overlay — rendered above everything when a game is active */}
      {activeGame && (
        <GamePlayerOverlay
          game={activeGame}
          onExit={handleExitGame}
          onRelaunch={handleRelaunchGame}
        />
      )}

      <div className="content">
        <div className="container">
          <div className="row no-gutter bg_op_secondary border_10 overflow-hidden">
            {/* ซ้าย: category nav */}
            <div className="col-2 col-lg-1 px-0">
              <div className="nav flex-column nav-pills nav_pills_lobby" id="v-pills-tab" role="tablist">
                {CATEGORIES.map((cat) => {
                  const IconComponent = cat.Icon
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      role="tab"
                      className={`nav-link ${activeCategory === cat.id ? 'active' : ''}`}
                      onClick={() => handleCategoryChange(cat.id)}
                    >
                      <IconComponent />
                      {cat.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* ขวา: provider tabs + game grid */}
            <div className="col-10 col-lg-11 px-0">
              {loggedIn && providers.length > 0 && (
                <div className="p-2">{renderProviderTabs()}</div>
              )}

              {loggedIn && activeProviderId !== ALL_PROVIDERS_ID && (
                <div className="px-2 pb-1">
                  <input
                    type="text"
                    placeholder="🔍 ค้นหาเกม..."
                    value={gameSearch}
                    onChange={(e) => setGameSearch(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: 280,
                      padding: '0.35rem 0.75rem',
                      borderRadius: 20,
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(0,0,0,0.3)',
                      color: '#fff',
                      fontSize: '0.875rem',
                      outline: 'none',
                    }}
                  />
                </div>
              )}

              {launchError && (
                <div
                  style={{
                    margin: '0.5rem',
                    padding: '0.5rem 1rem',
                    background: 'rgba(239,68,68,0.2)',
                    border: '1px solid rgba(239,68,68,0.5)',
                    borderRadius: 6,
                    color: '#fca5a5',
                    fontSize: '0.875rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>⚠️ {launchError}</span>
                  <button
                    style={{ background: 'transparent', border: 'none', color: '#fca5a5', cursor: 'pointer' }}
                    onClick={() => setLaunchError(null)}
                  >
                    ✕
                  </button>
                </div>
              )}

              <div className="p-2 bg_gd_secondary h-100">{renderGamesGrid()}</div>
            </div>
          </div>
        </div>

        <style>{`
          .lobby_link:hover .game-play-overlay { opacity: 1 !important; }
          .lobby_link { transition: transform 0.15s; }
          .lobby_link:hover { transform: translateY(-2px); }
        `}</style>
      </div>
    </>
  )
}

export default ModernLobbyCasinoPage
